import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import {
  CandidateEngagementsConst,
  CandidateOpportunitiesConst,
} from '@axiom/const';
import {
  Badge,
  Banner,
  Button,
  Card,
  CardSection,
  colors,
  DateUtil,
  FluidButtonLayout,
  Gutter,
  Tabs,
  TabsItem,
  useApi,
} from '@axiom/ui';

import TransparentVectorBkg from '../../public/svg/background-effect-1.svg?url';
import { CandidateEngagementsApi } from '../../api/candidate-engagements-api';
import { CandidateApi } from '../../api/candidate-api';
import { useCandidateId } from '../../hooks/useCandidateId';
import { EngagementHeader } from '../EngagementHeader/EngagementHeader';
import { WarmingFlow } from '../WarmingFlow/WarmingFlow';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import type { EngagementsViewSection } from '../../utils/engagements-util';
import {
  EngagementsUtil,
  EngagementsViewSections,
} from '../../utils/engagements-util';
import { EngagementDetails } from '../EngagementDetails/EngagementDetails';
import { CandidateOpportunitiesApi } from '../../api/candidate-opportunities-api';
import { SubmissionsViewDetails } from '../SubmissionsViewDetails/SubmissionsViewDetails';

import { EngagementsViewTeam } from './EngagementsViewTeam';
import { EngagementsViewStateBanner } from './EngagementsViewStateBanner';
import { EngagementsViewOptOutModal } from './EngagementsViewOptOutModal';

const { EngagementStates } = CandidateEngagementsConst;
const { CandidateStatuses } = CandidateOpportunitiesConst;

const OnboardingStatuses = new Set([
  CandidateStatuses.Selected,
  CandidateStatuses.Engaged,
]);

const SalesLeadVisibleStates = new Set([
  EngagementStates.InterviewingUnaccepted,
  EngagementStates.InterviewingClientUnacceptedTalentAccepted,
  EngagementStates.InterviewingExpectedExternal,
  EngagementStates.InterviewingExpectedManual,
  EngagementStates.InterviewingConfirmed,
  EngagementStates.Interviewed,
  EngagementStates.InterviewingCancelled,
]);

const tabLabels: Record<EngagementsViewSection, string> = {
  [EngagementsViewSections.upcomingEngagements]: 'Upcoming Engagements',
  [EngagementsViewSections.opportunities]: 'Opportunity Details',
  [EngagementsViewSections.submissions]: 'Submission Details',
};

export type EngagementsViewContentProps = {
  name?: string;
};

export const EngagementsViewContent = ({
  name = 'ENGAGEMENTS_VIEW_CONTENT',
}: EngagementsViewContentProps = {}) => {
  useDocumentTitle('Engagements');
  const candidateId = useCandidateId();
  const { opportunityId } = useParams();
  const [{ data: engagements }] = useApi(
    CandidateEngagementsApi.readEngagementsCurrent(candidateId)
  );
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isNoModalOpen, setIsNoModalOpen] = useState(false);
  const [isWarmingFlowOpen, setIsWarmingFlowOpen] = useState(false);
  const isValidToShow = engagements.some(
    engagement => engagement.opportunity.id === opportunityId
  );
  const isInOnboardingStatus = engagements.some(
    engagement =>
      engagement.opportunity.id === opportunityId &&
      OnboardingStatuses.has(engagement.candidateStatus)
  );
  const [
    { data: candidate },
    { data: engagement } = { data: null },
    { data: engagementDetails } = { data: null },
  ] = useApi(
    CandidateApi.readCandidate(candidateId),
    opportunityId && isValidToShow
      ? CandidateEngagementsApi.readEngagementsCurrentDetails(
          candidateId,
          opportunityId
        )
      : undefined,
    opportunityId && isInOnboardingStatus
      ? CandidateOpportunitiesApi.readCandidateOpportunityOnboarding({
          candidateId,
          opportunityId,
        })
      : undefined
  );

  const currentSection = engagement
    ? EngagementsUtil.getSectionFromEngagement({ engagement })
    : null;

  useEffect(() => {
    if (
      engagement &&
      searchParams.get('modal') === 'warming' &&
      opportunityId === engagement.opportunity.id &&
      (engagement.engagementState === EngagementStates.Warmed ||
        engagement.hasMovedPastWarmedWithoutAnsweringWarmingQuestions)
    ) {
      setIsWarmingFlowOpen(true);
    }

    setSearchParams({}, { replace: true });
  }, [engagement, searchParams]);

  return engagement ? (
    <div data-test={name}>
      <Card>
        <EngagementHeader
          name="ENGAGEMENT_HEADER"
          appendRight={
            engagement.engagementState ===
            CandidateEngagementsConst.EngagementStates.Interested ? (
              <Badge background="gray" name="INTERESTED_BADGE">
                Interested
                {engagement.candidateInterestedAt
                  ? ` ${DateUtil.displayDate(engagement.candidateInterestedAt)}`
                  : null}
              </Badge>
            ) : null
          }
          background={{
            image: TransparentVectorBkg,
            color: colors.controlHoverPrimary,
            position: 'right top',
            size: 'contain',
          }}
          engagement={engagement}
          textReversed
        />
      </Card>
      <Gutter bottom="16px" />
      {engagement.engagementState ? (
        <Gutter bottom="16px">
          <EngagementsViewStateBanner
            name="ENGAGEMENTS_VIEW_STATE_BANNER"
            engagement={engagement}
            onWarmedNo={() => {
              setIsNoModalOpen(true);
            }}
            onWarmedYes={() => {
              setIsWarmingFlowOpen(true);
            }}
          />
        </Gutter>
      ) : null}
      {engagement.hasMovedPastWarmedWithoutAnsweringWarmingQuestions ? (
        <Gutter bottom="16px" name="ENGAGEMENT_WARMING_QUESTIONS_BANNER">
          <Banner impact="high" type="critical" name="WARMING_QUESTIONS_BANNER">
            Please confirm some basic qualifications and update your
            availability for this opportunity.
            <Gutter bottom="24px" />
            <FluidButtonLayout stackableOn="mobile">
              <Button
                name="CONFIRM_DETAILS_BUTTON"
                onClick={() => {
                  setIsWarmingFlowOpen(true);
                }}
              >
                Confirm Details
              </Button>
            </FluidButtonLayout>
          </Banner>
        </Gutter>
      ) : null}
      <Tabs variant="card" name="ENGAGEMENT_DETAILS_TABS">
        {engagementDetails?.positions?.[0]?.candidateOpportunities?.[0]
          ?.onboardingEmailSentAt ? (
          <TabsItem name="ENGAGEMENT_DETAILS_TAB" label="Engagement Details">
            <CardSection>
              <EngagementDetails
                name="ENGAGEMENT_DETAILS"
                data={engagementDetails}
              />
            </CardSection>
          </TabsItem>
        ) : null}
        <TabsItem
          name="SUBMISSION_DETAILS_TAB"
          label={currentSection ? tabLabels[currentSection] : null}
        >
          <SubmissionsViewDetails
            submission={engagement}
            name="SUBMISSION_VIEW_DETAILS"
          />
        </TabsItem>
        {engagement.engagementState === EngagementStates.Interested ? null : (
          <TabsItem name="AXIOM_TEAM_TAB" label="Axiom team">
            <EngagementsViewTeam
              engagement={engagement}
              name="ENGAGEMENT_VIEW_TEAM"
            />
            {engagement.opportunity.salesLead &&
            SalesLeadVisibleStates.has(engagement.engagementState) ? (
              <CardSection name="ENGAGEMENTS_VIEW_CONTENT_OPP_SALES_LEAD">
                Questions about the opportunity requirements or client? Reach
                out to the engagement contact{' '}
                <a
                  data-test="ENGAGEMENTS_VIEW_CONTENT_OPP_SALES_LEAD_MAILTO"
                  href={`mailto:${engagement.opportunity.salesLead.email}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  {engagement.opportunity.salesLead.email}
                </a>
                .
              </CardSection>
            ) : null}
          </TabsItem>
        )}
      </Tabs>
      {isWarmingFlowOpen ? (
        <WarmingFlow
          candidate={candidate}
          engagement={engagement}
          name="WARMING_FLOW"
          onClose={() => {
            setIsWarmingFlowOpen(false);
          }}
        />
      ) : null}
      {isNoModalOpen ? (
        <EngagementsViewOptOutModal
          engagement={engagement}
          name="ENGAGEMENTS_VIEW_OPT_OUT_MODAL"
          onAfterSubmit={() => {
            setIsNoModalOpen(false);
            void navigate('..');
          }}
          onClose={() => {
            setIsNoModalOpen(false);
          }}
        />
      ) : null}
    </div>
  ) : null;
};
